import React from 'react';
import { useTranslation } from 'react-i18next';
import { Reorder, useDragControls } from 'motion/react';

import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

import Iconify from '../../../../theme/iconify/iconify';

function Answer({ type, answer, index, answers, setAnswers, correct, setCorrect, reorder }) {
    const [grab, setGrab] = React.useState(false);
    const [hover, setHover] = React.useState(false);

    const { t } = useTranslation();
    const controls = useDragControls();

    const markSingleCorrect = (index) => {
        const newCorrect = [...correct];
        newCorrect.fill(false);
        newCorrect[index] = true;
        setCorrect(newCorrect);
    }

    const markMultipleCorrect = (index) => {
        const newCorrect = [...correct];
        newCorrect[index] = !newCorrect[index];
        setCorrect(newCorrect);
    }

    const removeAnswer = (index) => {
        const newAnswers = [...answers];
        const newCorrect = [...correct];
        newAnswers.splice(index, 1);
        newCorrect.splice(index, 1);
        setAnswers(newAnswers);
        setCorrect(newCorrect);
    }

    const handleChange = (e) => {
        const newAnswers = [...answers];
        newAnswers[index] = e.target.value;
        setAnswers(newAnswers);
    }

    return (
        <Reorder.Item
            value={answer}
            dragListener={false}
            dragControls={controls}
            style={{
                listStyle: 'none',
                paddingTop: '8px',
                paddingBottom: '8px'
            }}>
            <Grid container alignItems='center'>
                <Grid item xs={0.5} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip
                        title={t('course.modals.newQuiz.questions.tooltips.reorder')}
                        open={!grab && hover}
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}
                        arrow
                    >
                        <Iconify
                            key={answer}
                            icon='lsicon:drag-filled'
                            width={26}
                            onPointerDown={(e) => {
                                controls.start(e);
                                setGrab(true);
                            }}
                            onPointerUp={(e) => {
                                reorder();
                                setGrab(false);
                                setHover(false);
                            }}
                            sx={{ cursor: grab ? 'grabbing' : 'grab' }}
                        />
                    </Tooltip>
                </Grid>
                <Grid item xs={11.5}>
                    {type === 'singleChoice' && (
                        <TextField
                            size='small'
                            value={answer}
                            onChange={(e) => handleChange(e)}
                            variant='outlined'
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position='start'
                                        onClick={() => markSingleCorrect(index)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <Tooltip title={t(`course.modals.newQuiz.questions.tooltips.${correct[index] ? 'correct' : 'setCorrect'}`)} arrow>
                                            <Iconify icon={correct[index] ? 'mdi:radio-button-checked' : 'mdi:radio-button-unchecked'} width={18} sx={{ color: 'primary.main' }} />
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment
                                        position='end'
                                        onClick={() => removeAnswer(index)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <Tooltip title={t('course.modals.newQuiz.questions.tooltips.removeAnswer')} arrow>
                                            <IconButton
                                                color='error'
                                                sx={{
                                                    p: '4px',
                                                    borderRadius: '8px'
                                                }}
                                            >
                                                <Iconify icon='eva:trash-outline' width={18} sx={{ color: 'error.main' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                )
                            }}
                        />
                    )}
                    {type === 'multipleChoice' && (
                        <TextField
                            size='small'
                            value={answer}
                            onChange={(e) => handleChange(e)}
                            variant='outlined'
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position='start'
                                        onClick={() => markMultipleCorrect(index)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <Tooltip title={t(`course.modals.newQuiz.questions.tooltips.${correct[index] ? 'setIncorrect' : 'setCorrect'}`)} arrow>
                                            <Iconify icon={correct[index] ? 'mingcute:checkbox-line' : 'fluent:checkbox-unchecked-24-filled'} width={18} sx={{ color: 'primary.main' }} />
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment
                                        position='end'
                                        onClick={() => removeAnswer(index)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <Tooltip title={t('course.modals.newQuiz.questions.tooltips.removeAnswer')} arrow>
                                            <Iconify icon='eva:trash-outline' width={18} sx={{ color: 'error.main' }} />
                                        </Tooltip>
                                    </InputAdornment>
                                )
                            }}
                        />
                    )}
                </Grid>
            </Grid>
        </Reorder.Item>
    );
}

export default Answer;